import NewOrderSection from "./new-order-section";
import OrderCustomerInformation from "./OrderCustomerInformation";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import CreateAddressForm from "./form/create-address";
import Autocomplete from "react-autocomplete";
import { Loader } from "../commons/loader";
import { Field } from "formik";
import { add } from "date-fns";
import {
  DEFAULT_ORDER_PICKUP_TIME,
  FULFILLMENT_METHOD_TYPES,
  MAX_ORDER_PICKUP_DAY,
  VERTICALS
} from "../../utils/constants";
import { DateField } from "../ui/form-fields/date-form-field";
import { pDateFormat, yyyyMMdd } from "../../utils/helper";
import HearAboutUs from "./hear-about-us";
import { toast } from "react-toastify";
import { default as _, isEmpty } from "lodash";
import DeleteCustomerAddress from "./DeleteCustomerAddress";
import yesImage from "../../assets/images/yes.svg";
import noImage from "../../assets/images/no.svg";
import { getOrderTimeSlotsAPI } from "../../api/order";

const transformOrderTimeSlots = (date, orderTimeSlots) => {
  const out = [];
  const dateStr = typeof date === "string" ? date : yyyyMMdd(date);
  for (const orderTimeSlot of orderTimeSlots) {
    out.push({
      ...orderTimeSlot,
      dateTime: new Date(`${dateStr}T${orderTimeSlot.time}`)
    });
  }
  out.sort((a, b) => a.dateTime - b.dateTime);
  return out;
};

const newScheduleRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  pickupDate: add(new Date(), { minutes: DEFAULT_ORDER_PICKUP_TIME }),
  timeSlotId: ""
});

/** Unique key for date + time slot (same calendar day + same slot id). */
const scheduleSlotKey = (pickupDate, timeSlotId) => {
  if (!pickupDate || timeSlotId === "" || timeSlotId == null) return null;
  return `${yyyyMMdd(pickupDate)}:${String(timeSlotId)}`;
};

const isDuplicateScheduleSlot = (rows, pickupDate, timeSlotId, excludeRowKey) => {
  const key = scheduleSlotKey(pickupDate, timeSlotId);
  if (!key) return false;
  return rows.some(
    row =>
      row.key !== excludeRowKey &&
      scheduleSlotKey(row.pickupDate, row.timeSlotId) === key
  );
};

const hasDuplicateScheduleSlots = rows => {
  const keys = rows
    .map(row => scheduleSlotKey(row.pickupDate, row.timeSlotId))
    .filter(Boolean);
  return keys.length !== new Set(keys).size;
};

const HomeCareBulkOrderCreateWrapper = props => {
  const {
    orderFormik,
    customerIds,
    getCustomersId,
    getCustomerDetail,
    customerDetail,
    resetCustomerState,
    onDiscard,
    createCustomerPatient,
    updateCustomerPatient,
    deleteCustomerPatient,
    createCustomer,
    ordersLoading,
    orderInitialValues,
    createCustomerAddress,
    cities,
    deleteCustomerAddress,
    scanTypes,
    getOrderScanTypes,
    scanTypesLoading,
    resetScanTypesState,
    hearAboutUsList,
    getHearAboutUsList,
    copiedOrder,
    history,
    regions,
    getOrderSources,
    orderSources,
    homeCareCategories,
    getHomeCareCategories,
    prescriptionAccept = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
    prescriptionFileValidate,
    homeCarePrescriptionFileRef,
    scheduleRows,
    setScheduleRows,
    slotsByRow,
    setSlotsByRow,
    slotsLoadingByRow,
    setSlotsLoadingByRow
  } = props;

  const [selectedPatient, setSelectedPatient] = useState({});
  const [isOpenCreateAddressModal, setIsOpenCreateAddressModal] =
    React.useState(false);
  const [isOpenHearAboutUsModal, setIsOpenHearAboutUsModal] = useState(false);
  const [test, setTest] = useState("");
  const [tests, setTests] = useState([]);
  const [isCovid, setIsCovid] = useState(false);
  const [isOpenDeleteCustomerAddressModal, setIsOpenDeleteCustomerAddressModal] =
    useState(false);
  const [customerAddressId, setCustomerAddressId] = useState(null);

  const testsTotal = _.sumBy(tests, t => t.value);
  const value = testsTotal - (+orderFormik.values.discount * testsTotal) / 100;

  const fetchSlotsForRow = async (rowKey, pickupDate) => {
    const region = orderFormik.values.region;
    const addr = orderFormik.values.customerAddress;
    if (!pickupDate || !region || !addr) {
      setSlotsByRow(prev => ({ ...prev, [rowKey]: [] }));
      return;
    }
    setSlotsLoadingByRow(prev => ({ ...prev, [rowKey]: true }));
    try {
      const resp = await getOrderTimeSlotsAPI({
        date: yyyyMMdd(pickupDate),
        regionId: region,
        isCovid,
        vertical: VERTICALS.homecare,
        customerAddressId: addr
      });
      if (resp && resp.data) {
        const transformed = transformOrderTimeSlots(
          yyyyMMdd(pickupDate),
          resp.data
        );
        setSlotsByRow(prev => ({ ...prev, [rowKey]: transformed }));
      }
    } catch {
      setSlotsByRow(prev => ({ ...prev, [rowKey]: [] }));
    } finally {
      setSlotsLoadingByRow(prev => ({ ...prev, [rowKey]: false }));
    }
  };

  useEffect(() => {
    scheduleRows.forEach(row => {
      if (!row.pickupDate) {
        setSlotsByRow(prev => ({ ...prev, [row.key]: [] }));
      } else {
        fetchSlotsForRow(row.key, row.pickupDate);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scheduleRows.map(r => `${r.key}:${r.pickupDate ? yyyyMMdd(r.pickupDate) : ""}`).join("|"),
    orderFormik.values.region,
    orderFormik.values.customerAddress,
    isCovid
  ]);

  const onCreateBulkOrders = async e => {
    if (!selectedPatient.id) {
      toast.error("Select Patient");
      return;
    }
    orderFormik.setFieldValue("patient", selectedPatient.id);
    if (!orderFormik.values.customerAddress) {
      toast.error("Create or select address");
      return;
    }
    if (!tests.length) {
      toast.error("Select test type");
      return;
    }
    for (const row of scheduleRows) {
      if (!row.pickupDate) {
        toast.error("Each row needs a pickup date");
        return;
      }
      if (!row.timeSlotId) {
        toast.error("Select a time slot for each date");
        return;
      }
      const slots = slotsByRow[row.key] || [];
      const ok = slots.some(s => String(s.id) === String(row.timeSlotId));
      if (!ok) {
        toast.error("Refresh time slots and pick a valid time for each date");
        return;
      }
    }
    if (hasDuplicateScheduleSlots(scheduleRows)) {
      toast.error(
        "Each visit must have a unique date and time. You cannot use the same time slot twice on the same day."
      );
      return;
    }
    orderFormik.setFieldValue("scanType", tests.map(test => test.code));
    orderFormik.setFieldValue("value", value);
    await orderFormik.validateForm();
    if (orderFormik.errors.fulfillmentMethod) {
      return toast.error("Please select order type");
    }
    if (orderFormik.errors.discount) {
      return toast.error(orderFormik.errors.discount);
    }
    if (orderFormik.errors.value) {
      return toast.error(orderFormik.errors.value);
    }
    if (orderFormik.errors.additionalNotes) {
      return toast.error(orderFormik.errors.additionalNotes);
    }
    if (orderFormik.errors.orderSource) {
      return toast.error("Please select order source");
    }
    if (orderFormik.errors.homeCareCategory) {
      return toast.error("Please select home care category");
    }
    if (customerDetail.hasFilledHearAboutUs) {
      await orderFormik.submitForm();
    } else {
      setIsOpenHearAboutUsModal(true);
    }
  };

  const confirmOrder = hearAboutUsId => {
    orderFormik.values.hearAbout = hearAboutUsId;
    setIsOpenHearAboutUsModal(false);
    orderFormik.submitForm();
  };

  const closeHearAboutUsModal = () => {
    setIsOpenHearAboutUsModal(false);
  };

  const searchOrderScanTypes = testQuery => {
    if (testQuery.length === 0) {
      resetScanTypesState();
      return;
    }
    getOrderScanTypes({ search: testQuery, vertical: VERTICALS.homecare });
  };

  useEffect(() => {
    if (!isEmpty(copiedOrder) && copiedOrder.customerId === customerDetail.id) {
      setTest("");
      setTests(copiedOrder.scanTypes);
      orderFormik.setFieldValue("urgentDelivery", copiedOrder.urgentDelivery);
      orderFormik.setFieldValue(
        "additionalNotes",
        copiedOrder.additionalNotes ? copiedOrder.additionalNotes : ""
      );
      if (copiedOrder.homeCareCategory) {
        orderFormik.setFieldValue("homeCareCategory", copiedOrder.homeCareCategory);
      }
      orderFormik.setFieldValue(
        "requiresFemaleStaff",
        !!copiedOrder.requiresFemaleStaff
      );
    }
  }, [customerDetail.id]);

  useEffect(() => {
    setIsCovid(tests.some(t => t.isCovid));
  }, [tests]);

  // Prefill patient-level allergy info from the selected patient so an existing
  // record is preserved (and shown) when the visits are created.
  useEffect(() => {
    if (selectedPatient && selectedPatient.id) {
      orderFormik.setFieldValue("hasAllergy", !!selectedPatient.hasAllergy);
      orderFormik.setFieldValue("allergies", selectedPatient.allergies || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient && selectedPatient.id]);

  useEffect(() => {
    getOrderSources();
    getHomeCareCategories();
  }, []);

  const updateRow = (rowKey, patch) => {
    setScheduleRows(rows =>
      rows.map(r => (r.key === rowKey ? { ...r, ...patch } : r))
    );
  };

  const addScheduleRow = () => {
    setScheduleRows(rows => [...rows, newScheduleRow()]);
  };

  const handleTimeSlotChange = (row, timeSlotId) => {
    if (!timeSlotId) {
      updateRow(row.key, { timeSlotId: "" });
      return;
    }
    if (
      isDuplicateScheduleSlot(
        scheduleRows,
        row.pickupDate,
        timeSlotId,
        row.key
      )
    ) {
      toast.error(
        "This date and time is already selected for another visit. Choose a different time slot."
      );
      return;
    }
    updateRow(row.key, { timeSlotId });
  };

  const removeScheduleRow = rowKey => {
    setScheduleRows(rows => {
      if (rows.length <= 1) return rows;
      return rows.filter(r => r.key !== rowKey);
    });
    setSlotsByRow(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const openModal = e => {
    e.preventDefault();
    if (!customerDetail.id) {
      toast.error("Create or Select Customer");
      return;
    }
    setIsOpenCreateAddressModal(true);
  };

  const closeModal = () => {
    setIsOpenCreateAddressModal(false);
  };

  const closeDeleteCustomerAddressModal = () => {
    setIsOpenDeleteCustomerAddressModal(false);
  };

  const handleCustomerAddressDeletion = id => {
    deleteCustomerAddress({ customerAddressId: id });
    closeDeleteCustomerAddressModal();
  };

  return (
    <>
      <div className="order-page">
        <NewOrderSection />
        <div className="order-right-info">
          <div className="head">
            <ul className="btn-list">
              <li>
                <button className="red" onClick={onDiscard}>
                  Discard
                </button>
              </li>
              <li>
                <button
                  className="yellow"
                  onClick={onCreateBulkOrders}
                  disabled={orderFormik.isValidating || ordersLoading}
                >
                  Create bulk orders
                </button>
              </li>
            </ul>
          </div>
          <OrderCustomerInformation
            selectedPatient={selectedPatient}
            setSelectedPatient={setSelectedPatient}
            orderFormik={orderFormik}
            customerIds={customerIds}
            customersLoading={props.customersLoading}
            getCustomersId={getCustomersId}
            getCustomerDetail={getCustomerDetail}
            customerDetail={customerDetail}
            history={history}
            resetCustomerState={resetCustomerState}
            createCustomerPatient={createCustomerPatient}
            updateCustomerPatient={updateCustomerPatient}
            deleteCustomerPatient={deleteCustomerPatient}
            createCustomer={createCustomer}
            ordersLoading={ordersLoading}
            orderInitialValues={orderInitialValues}
            copiedOrder={copiedOrder}
            createCustomerAddress={createCustomerAddress}
            cities={cities}
            deleteCustomerAddress={deleteCustomerAddress}
            vertical={VERTICALS.homecare}
          />
        </div>
      </div>

      <div className="order-page">
        <div className="order-aside step-two">
          <div className="head">
            <img src={require("../../assets/images/s-img.svg")} alt="" />{" "}
            <h2>Bulk schedule</h2>
          </div>
          <div>
            <div className="col-info order-col">
              <h2>Location</h2>
              <div className="form-row">
                <label>Region*</label>
                <div className="value select">
                  <select
                    name="region"
                    value={orderFormik.values.region}
                    disabled={
                      !!(orderFormik.values.region && orderFormik.values.customerAddress)
                    }
                    style={{ display: "block" }}
                    onChange={e => {
                      orderFormik.setFieldValue("region", e.target.value);
                      orderFormik.setFieldValue("customerAddress", "");
                    }}
                  >
                    <option value="" label="Select region" />
                    {regions.map(region => (
                      <option key={region.id} value={region.id} label={region.name} />
                    ))}
                  </select>
                </div>
              </div>

              <div className="order-address">
                <h2>Address</h2>
                <ul className="order-address-list">
                  {customerDetail.addresses.map(address => (
                    <li
                      key={address.id}
                      id={address.id}
                      className={
                        address.id === parseInt(orderFormik.values.customerAddress, 10)
                          ? "current"
                          : ""
                      }
                      onClick={e => {
                        orderFormik.setFieldValue("customerAddress", e.target.id);
                        orderFormik.setFieldValue(
                          "region",
                          address.regionCustomerAddresses &&
                            address.regionCustomerAddresses[0].region.id
                        );
                      }}
                    >
                      <div className="address-badge">
                        <img src={address.verified ? yesImage : noImage} alt="" />
                        <div className="address-text">
                          <b>
                            {address.regionCustomerAddresses &&
                              address.regionCustomerAddresses[0].region.name}
                          </b>
                          : {address.address}
                        </div>
                      </div>
                      <a
                        href=""
                        className="cancel icon-clear"
                        onClick={ev => {
                          ev.preventDefault();
                          setCustomerAddressId(address.id);
                          setIsOpenDeleteCustomerAddressModal(true);
                        }}
                      />
                    </li>
                  ))}
                  {props.customersLoading ? <Loader /> : null}
                </ul>
                <a className="add-address" onClick={openModal}>
                  Add New Address
                </a>
                <div id="wrapper" className="inner-page">
                  <Modal
                    isOpen={isOpenCreateAddressModal}
                    onRequestClose={closeModal}
                    contentLabel="Create address"
                  >
                    {customerDetail.id && (
                      <CreateAddressForm
                        customerId={customerDetail.id}
                        closeModal={closeModal}
                        getCustomerDetail={getCustomerDetail}
                        createCustomerAddress={createCustomerAddress}
                        cities={cities}
                        vertical={VERTICALS.homecare}
                      />
                    )}
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="order-right-info">
          <div className="head">
            <ul className="btn-list">
              <li>
                <button className="red" onClick={onDiscard}>
                  Discard
                </button>
              </li>
              <li>
                <button
                  className="yellow"
                  onClick={onCreateBulkOrders}
                  disabled={orderFormik.isValidating || ordersLoading}
                >
                  Create bulk orders
                </button>
              </li>
            </ul>
          </div>
          <div className="two-cols">
            <div className="col-info test-col">
              <h2>Test Types</h2>
              <div className="form-row autocomplete">
                <Autocomplete
                  placeholder="Add medical test types"
                  getItemValue={item => item.label}
                  items={scanTypes}
                  renderItem={(item, isHighlighted) =>
                    scanTypes.length ? (
                      <div
                        key={item.id}
                        style={{
                          background: isHighlighted ? "lightgray" : "white"
                        }}
                      >
                        {item.label}
                      </div>
                    ) : null
                  }
                  value={test}
                  onChange={e => {
                    setTest(e.target.value);
                    searchOrderScanTypes(e.target.value);
                  }}
                  onSelect={(val, sel) => {
                    if (!sel.isAllowed) {
                      toast.error(
                        "This test isn't allowed in home sampling. Please ask the patient to visit the branch"
                      );
                      return;
                    }
                    setTests([
                      ...tests,
                      {
                        id: sel.id,
                        name: sel.name,
                        code: sel.code,
                        index: tests.length - 1,
                        value: +sel.value,
                        isCovid: sel.isCovid
                      }
                    ]);
                    setTest("");
                  }}
                />
              </div>
              {!tests.length && (
                <div className="no-test-area">
                  <img
                    src={require("../../assets/images/no-test.png")}
                    alt="no test"
                  />
                </div>
              )}
              {scanTypesLoading && <Loader />}
              <ul className={`selected-tag ${tests.length ? "" : "no-data"}`}>
                {tests.map((t, index) => (
                  <li key={`${t.id}${index}`}>
                    {t.code} - {t.name} | (PKR {t.value})
                    <a
                      id={t.id}
                      onClick={() => {
                        setTests(tests.filter(x => x.index !== t.index));
                        setTest("");
                      }}
                      className="clear icon-clear"
                    />
                  </li>
                ))}
              </ul>
              <div className="test-total">
                <span className="title">Tests Total</span>
                <div className="price-holder">
                  <span className="amo">{testsTotal}</span>
                  <span className="pkr">PKR</span>
                </div>
              </div>
            </div>

            <div className="col-info order-col">
              <h2>Order Information</h2>
              <div className="form-row">
                <label>Discount</label>
                <div className="value">
                  <span className="rs">%</span>
                  <Field
                    type="text"
                    name="discount"
                    placeholder="Order Discount"
                    onChange={e => {
                      const discount = +e.target.value;
                      if (discount >= 0 && discount <= 100) {
                        orderFormik.setFieldValue("discount", discount);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>Order Type*</label>
                <div className="value select">
                  <Field
                    as="select"
                    placeholder="Order Type"
                    name="fulfillmentMethod"
                    style={{ display: "block" }}
                    onChange={e => {
                      orderFormik.setFieldValue("fulfillmentMethod", e.target.value);
                    }}
                  >
                    <option style={{ display: "none" }}>Select order type</option>
                    <option value={FULFILLMENT_METHOD_TYPES.ONLINE}>Online Order</option>
                    <option value={FULFILLMENT_METHOD_TYPES.OFFLINE}>Offline Order</option>
                  </Field>
                </div>
              </div>
              <div className="form-row">
                <label>Order Source*</label>
                <div className="value select">
                  <Field
                    as="select"
                    placeholder="Order Source"
                    name="orderSource"
                    style={{ display: "block" }}
                    onChange={e => {
                      orderFormik.setFieldValue("orderSource", e.target.value);
                    }}
                  >
                    <option>Select order source</option>
                    {orderSources &&
                      orderSources.map(orderSource => (
                        <option key={orderSource.id} value={orderSource.id}>
                          {orderSource.text}
                        </option>
                      ))}
                  </Field>
                </div>
              </div>
              <div className="form-row">
                <label>Home care category*</label>
                <div className="value select">
                  <Field
                    as="select"
                    placeholder="Home care category"
                    name="homeCareCategory"
                    style={{ display: "block" }}
                    onChange={e => {
                      const v = e.target.value;
                      orderFormik.setFieldValue(
                        "homeCareCategory",
                        v === "" ? null : +v
                      );
                    }}
                  >
                    <option value="">Select home care category</option>
                    {homeCareCategories &&
                      homeCareCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </Field>
                </div>
              </div>
              <div className="form-row">
                <label>Prescription (optional)</label>
                <div className="value">
                  <input
                    type="file"
                    accept={prescriptionAccept}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) {
                        if (homeCarePrescriptionFileRef) {
                          homeCarePrescriptionFileRef.current = null;
                        }
                        orderFormik.setFieldValue("prescriptionFile", null);
                        return;
                      }
                      if (prescriptionFileValidate) {
                        const { ok, message } = prescriptionFileValidate(f);
                        if (!ok) {
                          toast.error(message || "Invalid prescription file.");
                          e.target.value = "";
                          return;
                        }
                      }
                      if (homeCarePrescriptionFileRef) {
                        homeCarePrescriptionFileRef.current = f;
                      }
                      orderFormik.setFieldValue("prescriptionFile", f);
                    }}
                  />
                  {orderFormik.values.prescriptionFile && (
                    <span className="help-text" style={{ display: "block" }}>
                      {orderFormik.values.prescriptionFile.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <label>Estimated Time/Period</label>
                <div className="value">
                  <Field
                    type="text"
                    name="estimatedTime"
                    placeholder="e.g. 2 hours, 30 mins"
                  />
                </div>
              </div>
              <div className="test-total">
                <span className="title">FINAL ORDER VALUE (each visit)</span>
                <div className="price-holder">
                  <span className="amo">{value.toFixed(0)}</span>
                  <span className="pkr">PKR</span>
                </div>
              </div>

              <h3 style={{ marginTop: "1.5rem" }}>Visits (scheduled)</h3>
              <p className="help-text" style={{ marginBottom: "0.75rem" }}>
                Add a date per visit. Time slots load from availability for that date.
              </p>
              {scheduleRows.map(row => {
                const rowSlots = slotsByRow[row.key] || [];
                const loading = slotsLoadingByRow[row.key];
                return (
                  <div
                    key={row.key}
                    style={{
                      border: "1px solid #ddd",
                      padding: "12px",
                      marginBottom: "12px",
                      borderRadius: "4px"
                    }}
                  >
                    <div className="form-row">
                      <label>Pickup date*</label>
                      <div className="date-picker">
                        <span className="icon-calendar" />
                        <DateField
                          field={{
                            name: `pickupDate-${row.key}`,
                            value: row.pickupDate,
                            onChange: () => {},
                            onBlur: () => {}
                          }}
                          form={{
                            setFieldValue: () => {},
                            touched: {},
                            errors: {}
                          }}
                          minDate={add(new Date(), {
                            minutes: DEFAULT_ORDER_PICKUP_TIME
                          })}
                          disablePast
                          maxDate={add(new Date(), { days: MAX_ORDER_PICKUP_DAY })}
                          onChange={val => {
                            updateRow(row.key, {
                              pickupDate: val,
                              timeSlotId: ""
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <label>Available time*</label>
                      <div className="value select">
                        <select
                          value={row.timeSlotId}
                          style={{ display: "block" }}
                          disabled={!orderFormik.values.region || loading}
                          onChange={e =>
                            handleTimeSlotChange(row, e.target.value)
                          }
                        >
                          <option value="">
                            {loading
                              ? "Loading slots..."
                              : rowSlots.length
                                ? "Select time"
                                : "No slots — pick another date"}
                          </option>
                          {rowSlots.map(timeSlot => {
                            const slotTaken = isDuplicateScheduleSlot(
                              scheduleRows,
                              row.pickupDate,
                              timeSlot.id,
                              row.key
                            );
                            return (
                              <option
                                key={`${row.key}-${timeSlot.id}`}
                                value={timeSlot.id}
                                disabled={slotTaken}
                              >
                                {pDateFormat(timeSlot.dateTime)}
                                {slotTaken ? " (already selected)" : ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    {scheduleRows.length > 1 && (
                      <button
                        type="button"
                        className="red"
                        style={{ marginTop: 8 }}
                        onClick={() => removeScheduleRow(row.key)}
                      >
                        Remove visit
                      </button>
                    )}
                  </div>
                );
              })}
              <button type="button" className="yellow" onClick={addScheduleRow}>
                Add another date
              </button>

              <div className="delivery-box" style={{ marginTop: "1rem" }}>
                <label className="custom-checkbox">
                  <Field
                    type="checkbox"
                    name="urgentDelivery"
                    checked={orderFormik.values.urgentDelivery}
                  />
                  <span className="fake-checkbox" />
                  <span className="text">Require Urgent Delivery</span>
                </label>
                <label className="custom-checkbox">
                  <Field
                    type="checkbox"
                    name="requiresFemaleStaff"
                    checked={orderFormik.values.requiresFemaleStaff}
                  />
                  <span className="fake-checkbox" />
                  <span className="text">Female staff required</span>
                </label>
                <label className="custom-checkbox">
                  <Field
                    type="checkbox"
                    name="hasAllergy"
                    checked={orderFormik.values.hasAllergy}
                    onChange={e => {
                      const checked = e.target.checked;
                      orderFormik.setFieldValue("hasAllergy", checked);
                      if (!checked) {
                        orderFormik.setFieldValue("allergies", "");
                      }
                    }}
                  />
                  <span className="fake-checkbox" />
                  <span className="text">Allergy</span>
                </label>
              </div>
              {orderFormik.values.hasAllergy && (
                <div className="form-row">
                  <label>Patient Allergies</label>
                  <div className="value">
                    <Field
                      component="textarea"
                      cols="30"
                      rows="3"
                      name="allergies"
                      placeholder="List the patient's allergies"
                    />
                  </div>
                </div>
              )}
              <Field
                component="textarea"
                cols="30"
                rows="10"
                name="additionalNotes"
                placeholder="Additional Comments"
              />
            </div>
          </div>
        </div>
      </div>

      {isOpenHearAboutUsModal && (
        <Modal
          isOpen={isOpenHearAboutUsModal}
          onRequestClose={closeHearAboutUsModal}
          className="small-modal"
        >
          <HearAboutUs
            closeModal={closeHearAboutUsModal}
            hearAboutUsList={hearAboutUsList}
            getHearAboutUsList={getHearAboutUsList}
            loading={ordersLoading}
            confirmOrder={confirmOrder}
            orderFormik={orderFormik}
          />
        </Modal>
      )}
      {isOpenDeleteCustomerAddressModal && (
        <Modal
          isOpen={isOpenDeleteCustomerAddressModal}
          onRequestClose={closeDeleteCustomerAddressModal}
          className="small-modal"
        >
          <DeleteCustomerAddress
            customerAddressId={customerAddressId}
            handleCustomerAddressDeletion={handleCustomerAddressDeletion}
            closeModal={closeDeleteCustomerAddressModal}
          />
        </Modal>
      )}
    </>
  );
};

export default HomeCareBulkOrderCreateWrapper;
